import { useCallback, useEffect, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  tcSelectedDevAreaAtom,
  clonedUnitsAtom,
  type ClonedUnit,
  type TcSelectedDevArea,
} from 'pages/type-curves/type-curve-filters/atoms';
import type { Pieces } from 'components/geometry-editing';
import { polygonAreaAcres } from 'utils/geometry-measure';
import type { SurveyRow } from 'components/survey-description';
import { surveyRowsAtom } from './atoms';
import { useUnitBuilder } from './context';

// Synthetic ids for clones: negative and monotonically decreasing so they can
// never collide with a real (always-positive) development-area id.
let nextCloneId = -1;

// Deep-copy a `[lon, lat]` ring so edits can't reach the source array.
const copyRing = (ring: GeoJSON.Position[]): GeoJSON.Position[] =>
  ring.map((point) => [...point]);

// Deep-copy every ring of a `Pieces` boundary.
const copyPieces = (pieces: Pieces): Pieces => pieces.map(copyRing);

// Copy a survey row so the clone's rows are independent of the source's.
const copyRow = (row: SurveyRow): SurveyRow => ({
  ...row,
  quarterCalls: [...row.quarterCalls],
});

// The boundary ring the clone should carry — its largest piece by AREA, or null.
// Deliberately not by vertex count: a 4-corner rectangle can easily out-point a
// denser but smaller piece, which would hand the clone the wrong ring.
const largestPiece = (pieces: Pieces): GeoJSON.Position[] | null => {
  if (pieces.length === 0) return null;
  const area = (ring: GeoJSON.Position[]): number =>
    polygonAreaAcres({ type: 'Polygon', coordinates: [ring] });
  return pieces.reduce((a, b) => {
    return area(b) > area(a) ? b : a;
  });
};

// A "<base> (copy)" name unique among the given names — the navigator and the
// selection resolver key on the name, so duplicates would make prev/next
// ambiguous. Keeps the bare "(copy)" for the first clone (the mock's marker) and
// only numbers on a collision.
const uniqueCopyName = (base: string, taken: readonly string[]): string => {
  const first = `${base} (copy)`;
  if (!taken.includes(first)) return first;
  for (let n = 2; ; n++) {
    const candidate = `${base} (copy ${n})`;
    if (!taken.includes(candidate)) return candidate;
  }
};

/**
 * The Unit Builder's "Clone unit" — a frontend-only, mock-parity duplicate.
 *
 * Snapshots the selected unit's boundary, survey rows and azimuth into an
 * editable, independent client-side copy (never persisted), selects it, and
 * opens the builder on it as a draft. The clone joins the unit stepper via
 * `clonedUnitsAtom`; this hook also keeps its survey rows and shape in sync as
 * the operator edits, and toggles the builder's draft state as the selection
 * moves on and off a clone.
 *
 * @return {Function} The clone handler for the toolbar button.
 */
export const useCloneUnit = (): (() => void) => {
  const { shapeEdit } = useUnitBuilder();
  const source = useAtomValue(tcSelectedDevAreaAtom);
  const setSelected = useSetAtom(tcSelectedDevAreaAtom);
  const [rows, setRows] = useAtom(surveyRowsAtom);
  const [clones, setClones] = useAtom(clonedUnitsAtom);

  // Read live state through refs so the effects below don't re-run on every edit
  // for the wrong reason (and so the write-back never depends on `clones`).
  const clonesRef = useRef(clones);
  clonesRef.current = clones;
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const piecesRef = useRef(shapeEdit.pieces);
  piecesRef.current = shapeEdit.pieces;

  const onClone = useCallback(() => {
    if (!source) return;

    const name = uniqueCopyName(
      source.developmentAreaName,
      clonesRef.current.map((clone) => clone.name)
    );
    const id = nextCloneId;
    nextCloneId -= 1;

    // `shapeEdit.pieces` is the unit's TRUE polygon for a real unit (the modal
    // seeds it from `versions/current`), and the reshaped boundary once the user
    // edits — so it is always the better source. It is NOT gated on
    // `shapeEdit.dirty`: cloning a freshly-opened unit is the common case, and
    // that guard used to fall through to `source.ring`, which is only the frame
    // RECTANGLE (`buildRingFromFrame`) — the clone came out rectangular. The
    // rectangle stays as a last resort for a selection whose geometry never
    // resolved.
    const workingRing =
      shapeEdit.pieces.length > 0
        ? largestPiece(shapeEdit.pieces)
        : (source.ring ?? null);

    const selection: TcSelectedDevArea = {
      ...source,
      developmentAreaId: id,
      developmentAreaName: name,
      // Server reads a draft can share with the unit it copied — the gun barrel —
      // resolve through this, since the clone's own id is synthetic. Cloning a
      // clone keeps pointing at the original real unit rather than chaining.
      sourceDevelopmentAreaId:
        source.sourceDevelopmentAreaId ?? source.developmentAreaId,
      ring: workingRing ? copyRing(workingRing) : undefined,
      crossSectionEndPoints: source.crossSectionEndPoints
        ? {
            start: { ...source.crossSectionEndPoints.start },
            end: { ...source.crossSectionEndPoints.end },
          }
        : undefined,
    };
    const surveyRows = rowsRef.current.map(copyRow);
    const pieces = copyPieces(shapeEdit.pieces);

    const clone: ClonedUnit = { id, name, selection, surveyRows, pieces };
    setClones((prev) => [...prev, clone]);

    // Switch the builder to the clone: select it (which scopes the builder to a
    // draft — `builderScopedUnitAtom` reads `unitId: null` for a clone) and
    // restore its rows. The builder is already open, since Clone lives in it.
    setSelected(selection);
    setRows(surveyRows.map(copyRow));
  }, [source, shapeEdit, setClones, setSelected, setRows]);

  // Survey rows never carry across a unit change — the survey panel resets them
  // to empty on every selection (see `use-survey-area-save`), so a clone no longer
  // restores its own rows on (re)select.
  const selectedId = source?.developmentAreaId;

  // The builder's scoped unit follows the selection automatically now — a clone
  // reads as a draft (`unitId: null`) through `builderScopedUnitAtom`, and a real
  // unit (including a header prev/next step) scopes to itself — so no effect needs
  // to keep a separate builder id in step.

  // ── Write edits back into the clone registry ──
  // So a clone's boundary/survey edits survive stepping away and back. Only fires
  // on an EDIT of the already-selected clone (same id across renders), never on a
  // fresh (re)select — which would otherwise clobber the snapshot with stale rows.
  const prevSyncedIdRef = useRef<number | undefined>(selectedId);
  const pieces = shapeEdit.pieces;
  useEffect(() => {
    const isEditOfSameUnit =
      selectedId !== undefined && selectedId === prevSyncedIdRef.current;
    prevSyncedIdRef.current = selectedId;
    if (!isEditOfSameUnit) return;
    setClones((prev) => {
      const idx = prev.findIndex((c) => c.id === selectedId);
      if (idx < 0) return prev;
      const ring =
        largestPiece(pieces) ?? prev[idx].selection.ring ?? undefined;
      const next = [...prev];
      next[idx] = {
        ...prev[idx],
        pieces: copyPieces(pieces),
        surveyRows: rowsRef.current.map(copyRow),
        selection: {
          ...prev[idx].selection,
          ring: ring ? copyRing(ring) : prev[idx].selection.ring,
        },
      };
      return next;
    });
  }, [selectedId, pieces, rows, setClones]);

  return onClone;
};
